<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <AdminPageHeader
      title="Activity Logs"
      subtitle="Search and audit important system changes"
      icon="fas fa-history"
      gradient="blue"
    />

    <BRow class="py-2">
      <BCol
        cols="12"
        md="4"
      >
        <BFormGroup
          label="Search"
          label-for="filterSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterSearch"
            v-model="tableData.filter.search"
            placeholder="search action, model, description, actor"
            autocomplete="off"
            debounce="700"
            type="text"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="2"
      >
        <BFormGroup
          label="Action"
          label-for="filterAction"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <VSelect
            v-model="selected.action"
            input-id="filterAction"
            :options="actionOptions"
            label="name"
            clearable
            append-to-body
            :disabled="tableData.busy"
            :calculate-position="calculateDropPosition"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="2"
      >
        <BFormGroup
          label="Model"
          label-for="filterModel"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <VSelect
            v-model="selected.model"
            input-id="filterModel"
            :options="modelOptions"
            label="name"
            clearable
            append-to-body
            :disabled="tableData.busy"
            :calculate-position="calculateDropPosition"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="2"
      >
        <BFormGroup
          label="Actor"
          label-for="filterActor"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <VSelect
            v-model="selected.actor"
            input-id="filterActor"
            :options="actorOptions"
            label="name"
            clearable
            append-to-body
            :disabled="tableData.busy"
            :calculate-position="calculateDropPosition"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="1"
      >
        <BFormGroup
          label="From"
          label-for="filterDateFrom"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterDateFrom"
            v-model="tableData.filter.date_from"
            type="date"
            :disabled="tableData.busy"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="1"
      >
        <BFormGroup
          label="To"
          label-for="filterDateTo"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterDateTo"
            v-model="tableData.filter.date_to"
            type="date"
            :disabled="tableData.busy"
          />
        </BFormGroup>
      </BCol>
    </BRow>

    <CustomTable
      ref="tableData"
      :provider="tableProvider"
      :fields="tableData.fields"
      :filter="tableData.filter"
      :filter-on="tableData.filterOn"
      :sort-by="tableData.sortBy"
      :sort-desc="tableData.sortDesc"
      :sort-direction="tableData.sortDirection"
      :total-rows="tableData.totalRows"
      :current-page="tableData.currentPage"
      :per-page="tableData.perPage"
      :stacked="tableData.stacked"
      :busy="tableData.busy"
    >
      <template #cell(event)="{ item }">
        <BBadge :variant="eventVariant(item.event)">
          {{ item.event || 'unknown' }}
        </BBadge>
      </template>

      <template #cell(model)="{ item }">
        <span class="fw-bold">{{ item.model || 'N/A' }}</span>
      </template>

      <template #cell(description)="{ item }">
        <span class="text-break">{{ item.description || 'N/A' }}</span>
      </template>

      <template #cell(performed_by)="{ item }">
        <span>{{ item.performed_by || 'System' }}</span>
      </template>
    </CustomTable>
  </BContainer>
</template>

<script>
import { mapGetters } from 'vuex'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'

import AdminPageHeader from '@/components/core/AdminPageHeader.vue'
import { SAdminActivityLog } from '@/services'

export default {
  name: 'VAdminActivityLogs',

  components: { AdminPageHeader },

  mixins: [MxMisc, MxFormatter],

  middleware: ['auth', 'admin', 'superuser'],

  metaInfo: () => ({ title: 'Activity Logs' }),

  data () {
    return {
      selected: {
        action: null,
        model: null,
        actor: null
      },
      actionOptions: [],
      modelOptions: [],
      actorOptions: [],
      tableData: {
        busy: false,
        filter: {
          search: '',
          action: '',
          model: '',
          causer_id: '',
          date_from: '',
          date_to: ''
        },
        filterOn: [],
        stacked: this.isMobile(),
        sortBy: 'created_at',
        sortDesc: true,
        sortDirection: 'desc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 1, key: 'event', class: 'text-center min-w-100px', sortable: true },
          { mobile: 2, key: 'model', class: 'min-w-120px' },
          { mobile: 3, key: 'description', class: 'min-w-300px' },
          { mobile: 4, key: 'performed_by', label: 'Actor', class: 'min-w-180px' },
          { mobile: 5, key: 'created_at', label: 'Timestamp', class: 'min-w-170px', formatter: this.dateTimeShortFormatter, sortable: true },
          { mobile: 6, key: 'log_name', class: 'min-w-120px', sortable: true }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: mapGetters({
    currentUser: 'auth/user'
  }),

  watch: {
    'selected.action' (value) {
      this.tableData.filter.action = value?.id || ''
    },
    'selected.model' (value) {
      this.tableData.filter.model = value?.id || ''
    },
    'selected.actor' (value) {
      this.tableData.filter.causer_id = value?.id || ''
    }
  },

  mounted () {
    this.fetchFilterOptions()
  },

  methods: {
    eventVariant (event) {
      const variants = {
        created: 'success',
        updated: 'warning',
        deleted: 'danger'
      }

      return variants[event] || 'secondary'
    },

    async fetchFilterOptions () {
      return SAdminActivityLog.getFilterOptions()
        .then(({ data: response }) => {
          const data = response?.data || response

          this.actionOptions = (data?.actions || data?.events || []).map(action => ({ id: action, name: action }))
          this.modelOptions = (data?.models || []).map(model => ({ id: model, name: model }))
          this.actorOptions = (data?.actors || []).map(actor => ({
            id: actor.id,
            name: actor.username ? `${actor.name} (${actor.username})` : actor.name
          }))
        })
        .catch(() => {
          this.actionOptions = []
          this.modelOptions = []
          this.actorOptions = []
        })
    },

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableData.busy = true

      const payload = {
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search,
        action: filter.action || '',
        model: filter.model || '',
        causer_id: filter.causer_id || '',
        date_from: filter.date_from || '',
        date_to: filter.date_to || ''
      }

      return SAdminActivityLog.getList(payload)
        .then(({ data: response }) => {
          const data = response?.data || response

          this.tableData.totalRows = data?.total_rows || 0
          return data?.items || []
        })
        .catch(() => {
          this.tableData.totalRows = 0
          return []
        })
        .finally(() => {
          this.tableData.busy = false
        })
    }
  }
}
</script>
